import React from 'react';
import { Typography, Box, Grid, CircularProgress } from '@mui/material';
import { FaCheckSquare } from "react-icons/fa";
import { FaSquareXmark } from "react-icons/fa6";
import './RecommendationTile.css';

function RecommendationTile({ 
  planName, 
  monthlyPrice, 
  yearlyPrice, 
  skuDescription, 
  includedServices = [], 
  notIncludedServices = [], 
  maxHeight // Accept maxHeight as a prop
}) {
  const totalServices = includedServices.length + notIncludedServices.length;
  const matchScore = totalServices > 0 ? Math.round((includedServices.length / totalServices) * 100) : 0;

  const getCircleColor = (score) => {
    if (score >= 75) return '#4caf50'; // Green
    if (score >= 50) return '#ffeb3b'; // Yellow
    return '#f44336'; // Red
  };

  return (
    <Box 
      className="tile-container" 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: maxHeight || '100%', // Set height to maxHeight or default to 100%
        width: '100%', 
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', 
        borderRadius: '8px', 
        overflow: 'hidden',
        padding: '16px', // Add some padding for a cleaner look
        boxSizing: 'border-box' // Ensure padding does not add to the height
      }}
    >
      <Grid container spacing={2} sx={{ flexDirection: 'column', height: '100%' }}>
        
        {/* Product Header */}
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/Microsoft_logo.png" alt="Microsoft Logo" className="microsoft-logo" />
          <Typography variant="h6" className="product-title" sx={{ fontSize: '1.25rem', fontWeight: 'bold', flexGrow: 1 }}>
            {planName}
          </Typography>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress variant="determinate" value={matchScore} sx={{ color: getCircleColor(matchScore) }} thickness={5} size={50} />
            <Typography variant="caption" sx={{ position: 'absolute', fontWeight: 'bold', fontSize: '0.70rem' }}>
              {matchScore}%
            </Typography>
          </Box>
        </Grid>

        {/* Description */}
        <Grid item>
          <Typography 
            variant="body1" 
            className="product-description" 
            sx={{ 
              fontSize: '0.875rem', 
              marginBottom: '8px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'normal', 
              display: '-webkit-box', 
              WebkitBoxOrient: 'vertical', 
              WebkitLineClamp: 4 // Limits to 4 lines
            }}
          >
            <strong>Description:</strong> {skuDescription || 'N/A'}
          </Typography>
        </Grid>

        {/* Included Services */}
        <Grid item sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <FaCheckSquare color='#4caf50' />
            <Typography variant="body2" sx={{ fontWeight: 'bold', marginLeft: '4px' }}>Included Services:</Typography>
          </Box>
          <Box component="ul" sx={{ paddingLeft: '20px', textAlign: 'left' }}>
            {includedServices.length > 0 ? (
              includedServices.map((service, index) => (
                <li key={index} style={{ fontSize: '0.875rem', color: '#4caf50' }}>{service}</li>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">None</Typography>
            )}
          </Box>
        </Grid>

        {/* Not Included Services */}
        <Grid item sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <FaSquareXmark color='red' />
            <Typography variant="body2" sx={{ fontWeight: 'bold', marginLeft: '4px' }}>Not Included Services:</Typography>
          </Box>
          <Box component="ul" sx={{ paddingLeft: '20px', textAlign: 'left' }}>
            {notIncludedServices.length > 0 ? (
              notIncludedServices.map((service, index) => (
                <li key={index} style={{ fontSize: '0.875rem', color: '#f44336' }}>{service}</li>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">None</Typography>
            )}
          </Box>
        </Grid>

        {/* Pricing Section */}
        <Grid item sx={{ marginTop: 'auto' }}>
          <Box className="price-info" sx={{
            backgroundColor: '#b0e3f3',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>
              Monthly Price: <span style={{ color: '#0078D4' }}>${monthlyPrice || 'N/A'}</span>
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>
              Yearly Price: <span style={{ color: '#0078D4' }}>${yearlyPrice || 'N/A'}</span>
            </Typography>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
}

export default RecommendationTile;
